/**
 * Upload bytes to a project, get back a reference. Multipart, so a raw
 * authenticated fetch rather than `authFetch`, which forces a JSON content type.
 */

import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';

import { getToken } from '@verantos-dev/core-auth';
import { getBaseApiUrl, getGatewayApiVersion } from '@verantos-dev/core-constants';

import type { EpiAttachment } from '../types/epi-chat-types';
import { MAX_ATTACHMENT_MB } from '../utils/attachmentLimits';

const projectFileSchema = z.object({
  fileId: z.string().min(1),
  filename: z.string().min(1),
  mimeType: z.string().min(1),
  size: z.number().int().nonnegative(),
});

const projectFileResponseSchema = z.object({ data: projectFileSchema });

/** FastAPI's ``HTTPException`` body. */
const errorDetailSchema = z.object({ detail: z.string().min(1) });

const RETRY_MESSAGE = "Couldn't upload — remove it and try again.";

// A 413 raised by the ingress rather than the endpoint carries an HTML body, not `detail`.
const TOO_LARGE_MESSAGE = `That file is larger than ${MAX_ATTACHMENT_MB} MB.`;

// The only statuses whose `detail` is researcher copy; a 404's "not found" is not.
const RESEARCHER_COPY_STATUSES = new Set([400, 413]);

const uploadFailureMessage = async (response: Response): Promise<string> => {
  const fallback = response.status === 413 ? TOO_LARGE_MESSAGE : RETRY_MESSAGE;
  if (!RESEARCHER_COPY_STATUSES.has(response.status)) return fallback;
  try {
    const parsed = errorDetailSchema.safeParse(await response.json());
    return parsed.success ? parsed.data.detail : fallback;
  } catch {
    return fallback;
  }
};

/** Upload a file and return its reference. Throws `Error` with researcher-facing text. */
export const uploadProjectFile = async (args: {
  projectId: string;
  file: File;
}): Promise<EpiAttachment> => {
  const token = await getToken();
  const url = `${getBaseApiUrl()}/${getGatewayApiVersion()}/projects/${args.projectId}/files`;
  const form = new FormData();
  form.append('file', args.file);
  let response: Response;
  try {
    response = await fetch(url, {
      method: 'POST',
      // No Content-Type header — the browser sets multipart/form-data + boundary.
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: form,
    });
  } catch {
    // A refused or dropped connection reaches the card as "Failed to fetch" otherwise.
    throw new Error(RETRY_MESSAGE);
  }
  if (!response.ok) {
    throw new Error(await uploadFailureMessage(response));
  }
  return projectFileResponseSchema.parse(await response.json()).data;
};

/** Mutation wrapper for {@link uploadProjectFile}. */
export const useUploadProjectFile = () => useMutation({ mutationFn: uploadProjectFile });
